import type { PaintHit, WashHit } from "./sim/backpack";
import { WASH_RADIUS, WASH_TIME } from "./sim/tuning";

// Paint splats (#136), sim- and three-free: a fixed ring of slots the
// renderer (`render/paint.ts`) draws one instance per. A new splat takes the
// oldest slot once the ring is full, and the oldest few shrink away as the
// ring fills so none vanishes all at once. The power washer (#151) washes
// them off: each `wash` event takes paint off the splats near where its jet
// hits, shrinking them until they're gone.

/** How many splats stay on the park at once. */
export const SPLAT_CAP = 64;
/** Once the ring is full, the oldest this many are drawn smaller the older they are. */
export const SPLAT_FADE = 12;

export class SplatPool {
  /** One per slot, null until used. */
  readonly slots: (PaintHit | null)[];
  /** How much paint each slot has left, 1 (fresh) .. 0 (washed off). */
  private left: Float32Array;
  private next = 0;
  count = 0;

  constructor(readonly cap = SPLAT_CAP) {
    this.slots = new Array<PaintHit | null>(cap).fill(null);
    this.left = new Float32Array(cap);
  }

  /** Puts a splat in the oldest slot (or a free one); returns which. */
  add(hit: PaintHit): number {
    const i = this.next;
    this.slots[i] = hit;
    this.left[i] = 1;
    this.next = (i + 1) % this.cap;
    this.count = Math.min(this.cap, this.count + 1);
    return i;
  }

  /**
   * The washer's jet hitting `at` for `dt` seconds: every splat within
   * `WASH_RADIUS` loses that share of `WASH_TIME`'s paint, and one with
   * none left is gone. True if it touched any.
   */
  wash(at: WashHit, dt: number): boolean {
    let touched = false;
    const r2 = WASH_RADIUS * WASH_RADIUS;
    for (let i = 0; i < this.cap; i++) {
      const s = this.slots[i];
      if (!s || (s.x - at.x) ** 2 + (s.y - at.y) ** 2 + (s.z - at.z) ** 2 > r2) continue;
      touched = true;
      this.left[i]! -= dt / WASH_TIME;
      if (this.left[i]! <= 1e-6) this.slots[i] = null;
    }
    return touched;
  }

  clear(): void {
    this.slots.fill(null);
    this.next = 0;
    this.count = 0;
  }

  /** How big slot `i` is drawn, 0 (empty) to 1: the oldest `SPLAT_FADE` shrink once the ring is full. */
  scale(i: number): number {
    if (!this.slots[i]) return 0;
    // 0 is the oldest splat, count - 1 the newest.
    const age = (i - this.next + this.cap) % this.cap - (this.cap - this.count);
    const fade = this.count - (this.cap - SPLAT_FADE);
    // Washing (#151) shrinks it too, as it goes.
    const paint = this.left[i]!;
    if (fade <= 0 || age >= fade) return paint;
    // Filling up, they start shrinking a step at a time rather than all at once.
    return (paint * (age + 1 + SPLAT_FADE - fade)) / (SPLAT_FADE + 1);
  }
}
